import { computed, onMounted, onUnmounted, ref } from 'vue'
import {
  STATUSPAGE_URL,
  groupComponents,
  worstStatus,
  type StatusPageSummary,
} from '@/lib/statuspage'

const REFETCH_MS = 60_000

export function useStatuspage() {
  const summary = ref<StatusPageSummary | null>(null)
  const error = ref<unknown>(null)
  const isLoading = ref(true)
  let timer: number | null = null

  async function fetchSummary() {
    try {
      const response = await fetch(STATUSPAGE_URL, { cache: 'no-store' })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      summary.value = (await response.json()) as StatusPageSummary
      error.value = null
    } catch (err) {
      error.value = err
    } finally {
      isLoading.value = false
    }
  }

  onMounted(() => {
    void fetchSummary()
    timer = window.setInterval(fetchSummary, REFETCH_MS)
  })

  onUnmounted(() => {
    if (timer !== null) window.clearInterval(timer)
  })

  // Only surface error when we have no data to show. Stale data beats an
  // ErrorState flash on transient refetch failures.
  const hasError = computed(() => error.value !== null && summary.value === null)

  const status = computed(() => summary.value?.status ?? null)
  const componentGroups = computed(() => {
    const result = groupComponents(summary.value?.components ?? [])
    return {
      ungrouped: result.ungrouped,
      groups: result.groups.map((g) => ({ ...g, worst: worstStatus(g.children) })),
    }
  })
  const incidents = computed(() => summary.value?.incidents ?? [])
  const scheduledMaintenances = computed(() => summary.value?.scheduled_maintenances ?? [])
  const lastUpdated = computed(() => summary.value?.page?.updated_at ?? null)

  return {
    status,
    componentGroups,
    incidents,
    scheduledMaintenances,
    lastUpdated,
    isLoading,
    error: hasError,
    refetch: fetchSummary,
  }
}
